import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { LocalizationService, LocalizationModule } from '@abp/ng.core';
import {
  CellValueChangedEvent,
  ColDef,
  EditableCallbackParams,
  GetContextMenuItemsParams,
  MenuItemDef,
} from '@ag-grid-community/core';
import { EMPTY, Observable } from 'rxjs';
import {
  DocLineRole,
  DocLineUpdatedDto,
  SupplierQuoteDto,
  SupplierQuoteService,
} from '@digiwall/goliath-documents/proxy';
import { BaseThirdPartyDocLineEditor, LinesEditorComponent } from '../../../../components';
import { ObserverQueue } from '@digiwall/core';
import { VatType } from '@digiwall/goliath-core/proxy';
import { DocType } from '../../../base-docs/detail/doc-type';
@Component({
  selector: 'document-supplier-quote-lines-grid',
  templateUrl: './supplier-quote-lines-editor.component.html',
  imports: [CommonModule, LinesEditorComponent, LocalizationModule],
  providers: [ObserverQueue],
})
export class SupplierQuoteLinesEditorComponent extends BaseThirdPartyDocLineEditor<SupplierQuoteDto> {
  @ViewChild('lineGrid') lineGrid: LinesEditorComponent;

  @Input() set supplierQuote(value) {
    this._document = value;
    this.setLanguageCode();
  }

  @Input() set readonly(value) {
    this._readonly = value;
  }

  protected override handlingLinesUpdatedEvent = true;

  protected articleLineRole: DocLineRole = DocLineRole.SupplierQuoteArticle;
  protected textLineRole: DocLineRole = DocLineRole.DocText;
  protected chapterLineRole: DocLineRole = DocLineRole.DocChapter;

  protected override articleLookupFn = (term: string) =>
    this.articleService.getArticlePurchasableSelectionLookup(term);

  public columnDefs: Array<ColDef> = [];
  public actionBtns: any[] = [];

  constructor(
    localizationService: LocalizationService,
    private quoteService: SupplierQuoteService,
    injector: Injector,
    updateQueue: ObserverQueue,
  ) {
    super(localizationService, quoteService, injector, updateQueue);

    this.actionBtns = [{ ...this.getPrintActionButton(), visibleInReadonly: true }];
    this.initColDefs();
  }
  private initColDefs(): void {
    this.columnDefs = super.colDefs();
    this.columnDefs.push(
      ...([
        {
          field: 'isOptionnal',
          headerName: this.localizationService.instant('Doc::IsOptionnal'),
          headerTooltip: this.localizationService.instant('Doc::IsOptionnal:Tooltip'),
          cellRenderer: 'agCheckboxCellRenderer',
          cellEditor: 'agCheckboxCellEditor',
          sortable: false,
          editable: params => {
            return this.isCellEditable(params) && !this.isParentLineOptional(params);
          },
          suppressMovable: true,
          pinned: 'right',
          lockPinned: true,
          width: 75,
        },
        this.colDef_Quantity,
        this.colDef_ArticleSalesUnitCode(DocType.Purchase),
        this.colDef_UnitPrice,
        ...this.getColDef_StockColumns(),
        this.colDef_Discount,
        this.colDef_VatCode(VatType.Purchase),
        this.colDef_TotalPrice,
        this.colDef_UnitWeight,
      ] as Array<ColDef>),
    );
  }

  isChapter = line => line.lineRole == DocLineRole.DocChapter;

  public override isCellEditable(params: EditableCallbackParams<any, any>) {
    let isEditable = true;

    if (params.data.lineRole == this.articleLineRole && params.colDef.field != 'code') {
      isEditable = params.data.articleId != null;
    }

    return isEditable && super.isCellEditable(params);
  }

  protected getContextMenuItems = (params: GetContextMenuItemsParams): (string | MenuItemDef)[] => {
    if (this._readonly) return [];

    return this.contextMenuItems(params);
  };

  protected getCellValueChangedObservable(
    event: CellValueChangedEvent<any, any>,
  ): Observable<DocLineUpdatedDto> {
    let observable: Observable<DocLineUpdatedDto> = super.getCellValueChangedObservable(event);
    if (observable == EMPTY) {
      switch (event.column.getColId()) {
        case 'unitPrice':
          observable = this.quoteService.setLineUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitPrice,
          );
          break;
        case 'articleStockUnitPrice':
          observable = this.quoteService.setLineStockUnitPrice(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.articleStockUnitPrice,
            true,
          );
          break;
        case 'discount':
          observable = this.quoteService.setLineDiscount(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.discount,
          );
          break;
        case 'unitWeight':
          observable = this.quoteService.setLineUnitWeight(
            this._document.id,
            this._document.concurrencyStamp,
            event.data.id,
            event.data.unitWeight,
          );
          break;
        default:
          observable = EMPTY;
          break;
      }
    }
    return observable;
  }

  protected onCellValueChanged = (
    event: CellValueChangedEvent<any, any>,
  ): void | PromiseLike<void> => {
    let obs: Observable<DocLineUpdatedDto> = this.getCellValueChangedObservable(event);

    this._updateQueue
      .enqueue(() => obs)
      .subscribe({
        next: response => {
          if (response) this._updateLines(response);
        },
        error: _ => {
          let oldData = event.data;
          oldData[event.colDef.field] = event.oldValue;
          this.lineGrid.grid.grid.api.applyTransaction({
            update: [oldData],
          });
        },
      });
  };
}
